import React, { useState, useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // [NEW IMPORT]
import Badge from '@mui/material/Badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Link from '@mui/material/Link';
import BrandLogo from './EmpBrandLogo'; 
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar'; 
import { useTenant } from '../contexts/TenantContext'; 
import Skeleton from '@mui/material/Skeleton'; 
import ShoutOutForm from './ShoutOutForm'; 

import logger from '../utils/logger';

const MASTER_TENANT_ID = "e1a10000-0000-0000-0000-000000000000";
function Navbar() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, isAuthenticated, logout, isLoading, authFetch } = useAuth(); 
    const { tenantConfig, loading: tenantLoading } = useTenant();
    
    const logoTarget = useMemo(() => {
        if (!isAuthenticated) return "/";
        return null; // Passive for logged-in users
    }, [isAuthenticated]);

    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const [notificationsAnchor, setNotificationsAnchor] = useState(null);
    const [openShoutOut, setOpenShoutOut] = useState(false); 

    const { data: notifications = [], isError, isLoading: notificationsLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => authFetch('/api/notifications/my'),
        enabled: !!user && isAuthenticated,
        staleTime: 60 * 1000, 
        refetchInterval: 60 * 1000, 
        onSuccess: (data) => {
            logger.info('Successfully fetched notifications.', data);
        },
        onError: (error) => {
            logger.error('Failed to fetch notifications:', error);
        },
    });

    const unreadCount = useMemo(() => notifications.filter(n => !n.is_read).length, [notifications]);

    const markAllAsReadMutation = useMutation({
        mutationFn: () => authFetch('/api/notifications/mark-all-as-read', { method: 'POST' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const handleOpenProfileMenu = (event) => setProfileMenuAnchor(event.currentTarget);
    const handleCloseProfileMenu = () => setProfileMenuAnchor(null);
    const handleOpenNotifications = (event) => {
        setNotificationsAnchor(event.currentTarget);
        if (unreadCount > 0) {
            markAllAsReadMutation.mutate();
        }
    };
    const handleCloseNotifications = () => setNotificationsAnchor(null);
    
    const handleLogout = () => {
        logout();
        handleCloseProfileMenu();
    };

    const handleNotificationClick = (notification) => {
        handleCloseNotifications();
        if (notification.related_entity_type === 'PatientContentAssignment' && notification.related_entity_id) {
            navigate(`/assignment/${notification.related_entity_id}`);
        } else {
            navigate('/'); 
        }
    };

    const userProfilePic = user?.profile_image_url || user?.picture || user?.avatar_url;
    
    // LANDING PAGE COLOR PALETTE
    const NAV_TEXT_COLOR = '#111827'; // Gray-900
    const NAV_ICON_COLOR = '#475569'; // Slate-600
    const NAV_HOVER_BG = 'rgba(0, 0, 0, 0.05)';

    // Check if we are currently ON the Guardian Portal
    const isGuardianPortal = location.pathname === '/guardian-portal';

    // Shared 3D Button Style
    const navButtonStyle3D = {
        // 1. Solid Blue 3D Look
        background: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)', 
        color: 'white',
        
        // 2. Efficient Geometry (Squeezed)
        borderRadius: '8px', // Reduced from 20px to save side space
        textTransform: 'none', 
        fontWeight: 700,
        fontSize: '0.85rem', 
        px: 1.5, // Tighter padding
        py: 0.5, 
        minWidth: 'auto',
        mr: 1,
        
        // 3. 3D Shadow & Depth
        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)', 
        border: '1px solid #1d4ed8', 
        
        // 4. Interaction Physics
        transition: 'all 0.2s ease',
        '&:hover': { 
            background: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 12px -1px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
        },
        '&:active': {
            transform: 'translateY(1px)',
            boxShadow: '0 2px 4px -1px rgba(37, 99, 235, 0.4)',
        }
    };

    return (
        <AppBar position="fixed" sx={{
            background: 'rgba(255, 255, 255, 0.8)', 
            backdropFilter: 'blur(12px)',           
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
            borderBottom: '1px solid #f1f5f9', 
            color: NAV_TEXT_COLOR,
            zIndex: (theme) => theme.zIndex.drawer + 1,
        }}>

            <Toolbar disableGutters sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Tenant Logo Logic remains for multi-tenancy support */}
                    {!tenantLoading && (tenantConfig.logo_url && tenantConfig.id !== MASTER_TENANT_ID) && (
                        <Box
                            component="img"
                            src={`${process.env.REACT_APP_API_BASE_URL || ''}${tenantConfig.logo_url}`}
                            alt="Tenant Logo"
                            sx={{ height: '36px', marginRight: '12px', objectFit: 'contain' }}
                        />
                    )}

<Link
    component={RouterLink}
    to="/"
    onClick={(e) => {
        // Force navigation to the root domain if currently on a subdomain
        if (window.location.hostname.startsWith('e1p.')) {
            e.preventDefault();
            window.location.href = 'https://www.medsai.us/';
        } else if (window.location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }}
    sx={{ 
        textDecoration: 'none', 
        display: 'flex', 
        alignItems: 'center',
        cursor: 'pointer'
    }}
>
    <BrandLogo isNavbar={true} />
</Link>

                </Box>

                {isAuthenticated && !isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 } }}>
                        
                        {/* [MODIFIED] Intelligent 3D Button (Back vs Dashboard) */}
                        {user?.wards?.length > 0 && (
                            isGuardianPortal ? (
                                <Button
                                    component={RouterLink}
                                    to="/mymeds/patient"
                                    startIcon={<ArrowBackIcon sx={{ fontSize: '1rem !important' }} />}
                                    sx={navButtonStyle3D}
                                >
                                    Back
                                </Button>
                            ) : (
                                <Button
                                    component={RouterLink}
                                    to="/guardian-portal"
                                    sx={navButtonStyle3D}
                                >
                                    Dashboard
                                </Button>
                            )
                        )}

                        <IconButton 
                            size="large" 
                            aria-label="show new notifications" 
                            onClick={handleOpenNotifications}
                            sx={{ color: NAV_ICON_COLOR }}
                        >
                            <Badge badgeContent={unreadCount} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                        <Popover
                            open={Boolean(notificationsAnchor)}
                            anchorEl={notificationsAnchor}
                            onClose={handleCloseNotifications}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{ sx: { maxHeight: 400, width: 360 } }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                                <Typography variant="h6" component="div">Notifications</Typography>
                            </Box>
                            <Divider />
                            {notificationsLoading ? (
                                <ListItem>
                                    <ListItemText primary="Loading..." />
                                </ListItem>
                            ) : isError ? (
                                <Typography sx={{ p: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                                    No new notifications yet, great job following the nudges!
                                </Typography>
                            ) : notifications.length > 0 ? (
                                <List dense sx={{ p: 0 }}>
                                    {notifications.slice(0, 7).map((n) => (
                                        <MenuItem key={n.id} onClick={() => handleNotificationClick(n)} sx={{ opacity: n.is_read ? 0.6 : 1, whiteSpace: 'normal' }}>
                                            <ListItemText primary={n.title} secondary={n.body} />
                                        </MenuItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography sx={{ p: 2, color: 'text.secondary' }}>You have no notifications.</Typography>
                            )}
                        </Popover>

                        <IconButton 
                            size="large" 
                            aria-label="account of current user" 
                            aria-controls="menu-appbar" 
                            aria-haspopup="true" 
                            onClick={handleOpenProfileMenu} 
                            sx={{ color: NAV_ICON_COLOR }}
                        >
                            {userProfilePic ? (
                                <Avatar 
                                    src={userProfilePic} 
                                    alt={user?.first_name || 'User'}
                                    sx={{ width: 32, height: 32, border: '1px solid #e2e8f0' }}
                                />
                            ) : (
                                <AccountCircleIcon sx={{ fontSize: 32 }} />
                            )}
                        </IconButton>
                        <Menu id="menu-appbar" anchorEl={profileMenuAnchor} open={Boolean(profileMenuAnchor)} onClose={handleCloseProfileMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <MenuItem onClick={() => {
                                handleCloseProfileMenu();
                                setOpenShoutOut(true); 
                            }}>Appreciation/ Feedback</MenuItem>
                            
                            <MenuItem onClick={handleCloseProfileMenu} component={RouterLink} to="/forgot-password">Forgot Password</MenuItem>
                            <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </Menu>
                    </Box>
                )}
            </Toolbar>
            
            <ShoutOutForm open={openShoutOut} onClose={() => setOpenShoutOut(false)} />
        </AppBar>
    );
}

export default Navbar;